import React, { useEffect, useState } from "react";
import httpService from "../../../services/http/stores/outlet.service";
import UnlimitInput from "../../../components/ui-kit/unlimit-input";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import locationAPI from "../../../services/http/location.service";
import { all_routes } from "../../../Router/all_routes";
import notificationService from "../../../services/toaster.service";
import { Link } from "react-router-dom/dist";
import UnlimitSelect from "../../../components/ui-kit/unlimit-select";
import UnlimitSubmitButton from "../../../components/ui-kit/unlimit-submit-button";
import { hydrateErrors } from "../../../utils/error.utils";
import UnlimitLoading from "../../../components/ui-kit/unlimit-loading";
import { store } from "../../../core/redux/store";
import UnlimitBreadcrumb from "../../../components/ui-kit/unlimit-breadcrumb";
import UnlimitFileUpload from "../../../components/ui-kit/unlimit-fileupload";
import GoogleMaps from "../../../components/google-maps";
import UnlimitToggle from "../../../components/ui-kit/unlimit-toggle";
import { useTranslation } from "react-i18next";

const OutletsForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { t } = useTranslation();
    const { auth, locations } = store.getState();

    // Form state
    const {
        register,
        handleSubmit,
        formState,
        setValue,
        getValues,
        control,
        setError,
        watch,
    } = useForm({
        defaultValues: {
            is_active: true,
        },
    });
    const { errors } = formState;

    // UI states
    const [imageUrl, setImageUrl] = useState("");
    const [existingImageUrl, setExistingImageUrl] = useState("");
    const [imageUrlAttachments, setImageUrlAttachments] = useState("");
    const [existingImageUrlAttachments, setExistingImageUrlAttachments] =
        useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(false);

    // Location states
    const [center, setCenter] = useState({ lat: 55.04434, lng: 9.41741 });
    const [countries, setCountries] = useState([]);
    const [country, setCountry] = useState([]);
    const [cities, setCities] = useState([]);
    const [city, setCity] = useState([]);
    const [citiesLoading, setCitiesLoading] = useState(false);
    const [timezones, setTimezones] = useState([]);

    // File handling
    const watchFile = watch("attachment");

    useEffect(() => {
        if (watchFile?.length > 0) {
            setImageUrlAttachments(URL.createObjectURL(watchFile[0]));
        }
    }, [watchFile]);

    useEffect(() => {
        if (city) {
           // fetchCoordinates(city);
        }
    }, [city]);

    useEffect(() => {
        setCountries(locations.countries);
        if (id) {
            getOutlets();
        }
    }, [id, locations.countries]);

    // Fetch coordinates from city
    const fetchCoordinates = async (city) => {
        try {
            if (city.latitude && city.longitude) {
                const lat = parseFloat(city.latitude);
                const lng = parseFloat(city.longitude);
                setCenter({ lat, lng });
            }
        } catch (error) {
            console.error("Error fetching coordinates:", error);
        }
    };

    // Remove image
    const removeImage = () => {
        setImageUrlAttachments("");
        setExistingImageUrlAttachments("");
        setValue("image", "");
    };

    // Handle image change
    const handleImageChange = (event) => {
        const files = event.target.files;
        if (files.length > 0) {
            setImageUrl(URL.createObjectURL(files[0]));
        }
    };

    // Fetch outlet details for edit mode
    const getOutlets = async () => {
        try {
            setPageLoading(true);
            const response = await httpService.getOutlet(id);

            if (response?.status === 200) {
                const data = response.data.data;
                hydratePredefinedValues(data);
                await getCities(data.country.id);
            }
        } catch (error) {
            console.error("Error fetching outlet:", error);
            notificationService.errorMessage(t("outlet.fetchError"));
        } finally {
            setPageLoading(false);
        }
    };

    // Set form values from API data
    const hydratePredefinedValues = (data) => {
        // Basic details
        setValue("name", data.name);
        setValue("phone", data.phone);
        setValue("email", data.email);
        setValue("outlet_id", data.outlet_id);
        setValue("designation_id", data.designation_id);
        setValue("address", data.address);
        setValue("zip_code", data.zip_code);
        setValue("is_active", data.is_active == 1);
        setValue("city_id", data.city.id);
        setValue("country_id", data.country?.id);
        setValue("contact_name", data.contact_name);

        // Location details
        setValue("longitude", data.longitude);
        setValue("latitude", data.latitude);
        setCenter({
            lat: parseFloat(data.latitude),
            lng: parseFloat(data.longitude),
        });

        // Controller details
        setValue("n_site_id", data.n_site_id);
        setValue("n_controller_id", data.n_controller_id);
        setValue("n_control_type", data.n_control_type);
        setValue("n_control_index", data.n_control_index);


        // Images
        setExistingImageUrl(data.image);
        setExistingImageUrlAttachments(data.attachments?.[0]?.file);

        // Set timezone
        const allTimeZones = locations.countries
            ?.filter((country) => country.flag)
            ?.find((country) => country?.id == data?.country?.id)?.timezones;

        if (allTimeZones) {
            const parsedTimeZone = JSON.parse(allTimeZones);
            setTimezones(parsedTimeZone);

            const selectedTimezone = parsedTimeZone?.find(
                (timezone) => timezone?.gmtOffsetName == data?.timezone
            );

            if (selectedTimezone) {
                setValue("time_zone", {
                    ...selectedTimezone,
                    label: selectedTimezone?.zoneName,
                    value: selectedTimezone?.gmtOffsetName,
                });
            }
        }

        // Set selected country
        if (data.country.id) {
            setCountries((prevCountries) => {
                const country = prevCountries.find(
                    (item) => item.id === data.country.id
                );
                if (country) {
                    const formattedCountry = {
                        ...country,
                        label: country.name,
                        value: country.id,
                    };
                    setValue("country_id", formattedCountry);
                    setCountry(formattedCountry);
                }
                return prevCountries;
            });
        }
    };

    // Fetch cities for a country
    const getCities = async (country_id) => {
        try {
            setCitiesLoading(true);
            setCities([]);

            const response = await locationAPI.getCitiesByCountry(country_id);

            if (response?.status === 200) {
                const citiesData = response.data.data?.data || [];
                setCities(citiesData);

                if (id) {
                    const cityId = getValues("city_id");
                    const selectedCity = citiesData.find((item) => item.id === cityId);

                    if (selectedCity) {
                        const formattedCity = {
                            ...selectedCity,
                            label: selectedCity.name,
                            value: selectedCity.id,
                        };
                        setValue("city", formattedCity);
                        setCity(formattedCity);
                    }
                }
            }
        } catch (error) {
            console.error("Error fetching cities:", error);
        } finally {
            setCitiesLoading(false);
        }
    };

    // Handle country change
    const onChangeCountry = (value) => {
        if (value) {
            getCities(value.id);
            setCountry(value);

            try {
                const parsedTimezones = JSON.parse(value.timezones);
                setTimezones(parsedTimezones);
            } catch (error) {
                console.error("Error parsing timezones:", error);
                setTimezones([]);
            }
        }
    };

    // Handle city change
    const onChangeCity = (value) => {
        if (value) {
            setCity(value);
        }
    };

    // Form submission handler
    const onSubmitForm = async (data) => {
        try {
            setIsLoading(true);
            const formData = new FormData();

            // Basic details
            formData.append("name", data.name);
            formData.append("created_by", auth.user.id);
            formData.append("phone", data.phone);
            formData.append("email", data.email);
            formData.append("address", data.address);
            formData.append("owner_name", data.contact_name);
            formData.append("contact_name", data.contact_name);
            formData.append("zip_code", data.zip_code);
            formData.append("is_active", data.is_active ? 1 : 0);

            // Location details
            formData.append("longitude", center.lng);
            formData.append("latitude", center.lat);

            // Timezone
            if (data.time_zone?.value) {
                formData.append("time_zone", data.time_zone.value);
            }

            // Controller details
            formData.append("n_site_id", data.n_site_id);
            formData.append("n_controller_id", data.n_controller_id);
            formData.append("n_control_type", data.n_control_type);
            formData.append("n_control_index", data.n_control_index);

            // Image
            if (data.image?.[0]) {
                formData.append("image", data.image[0]);
            }

            // Location IDs
            if (data.country_id?.id) {
                formData.append("country_id", data.country_id.id);
            }

            if (data.city?.id) {
                formData.append("city_id", data.city.id);
            }

            // Handle update vs create
            if (id) {
                formData.append("id", id);
                await updateOutlet(formData);
            } else {
                await createOutlet(formData);
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Update existing outlet
    const updateOutlet = async (formData) => {
        try {
            const response = await httpService.updateOutlet(formData);

            if (response?.status === 200) {
                notificationService.successMessage(t("shop.shopUpdateSuccess"));
                setTimeout(() => {
                    navigate(all_routes.outletsList);
                }, 1000);
            }
        } catch (error) {
            if (error.validation_error?.errors) {
                hydrateErrors(error.validation_error.errors, setError);
            }
            console.error("Error updating outlet:", error);
        }
    };

    // Create new outlet
    const createOutlet = async (formData) => {
        try {
            const response = await httpService.createOutlet(formData);

            if (response?.status === 200) {
                notificationService.successMessage(t("shop.shopCreateSuccess"));
                navigate(all_routes.outletsList);
            }
        } catch (error) {
            if (error.validation_error?.errors) {
                hydrateErrors(error.validation_error.errors, setError);
            }
            console.error("Error creating outlet:", error);
        }
    };

    // Breadcrumb navigation
    const breadcrumbItems = [
        { label: "", link: "/", icon: "feather-home" },
        { label: t("shop.shops"), link: "/outlets-list" },
        { label: id ? t("shop.editShop") : t("shop.addShop"), active: true },
    ];

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Page Header */}
                <div className="page-header">
                    <div className="add-item d-flex flex-column">
                        <div className="page-title">
                            <h3 className="text-black">{t("shop.shop")}</h3>
                        </div>
                        <UnlimitBreadcrumb items={breadcrumbItems} />
                    </div>
                </div>

                {/* Outlet Form */}
                <form onSubmit={handleSubmit(onSubmitForm)}>
                    <div className="card">
                        {/* Form Header */}
                        <div className="card-body pb-0">
                            <h4>{t("shop.shopDetails")}</h4>
                        </div>
                        <hr />

                        {/* Form Content */}
                        <div className="card-body pb-0">
                            {pageLoading ? (
                                <div className="row d-flex justify-content-center py-4">
                                    <UnlimitLoading width={50} height={50} />
                                </div>
                            ) : (
                                <div>
                                    {/* Name and Image */}
                                    <div className="row mb-3">
                                        <div className="col-md-6">
                                            <UnlimitInput
                                                label={t("inputField.label.shopName")}
                                                id="name"
                                                type="text"
                                                placeholder={t("inputField.placeholder.enterName")}
                                                register={register}
                                                rules={{
                                                    required: t("inputField.validation.required"),
                                                    maxLength: {
                                                        value: 50,
                                                        message: t("inputField.validation.maxLength", {
                                                            value: 50,
                                                        }),
                                                    },
                                                }}
                                                error={errors.name}
                                                errorMessage={errors.name?.message}
                                            />
                                        </div>
                                        <div className="col-md-6 d-flex align-items-start justify-content-between">
                                            <UnlimitFileUpload
                                                label={t("inputField.label.image")}
                                                id="image"
                                                register={register}
                                                rules={{
                                                    required: !id && t("inputField.validation.required"),
                                                }}
                                                accept=".jpeg, .png, .jpg"
                                                error={errors.image}
                                                errorMessage={errors.image?.message}
                                                onChange={handleImageChange}
                                            />
                                            {(id || imageUrl) && (
                                                <img
                                                    src={id ? imageUrl || existingImageUrl : imageUrl}
                                                    alt={t("inputField.label.image")}
                                                    className="img-fluid"
                                                    style={{ maxWidth: "70px" }}
                                                />
                                            )}
                                        </div>
                                    </div>

                                    {/* Contact Information */}
                                    <div className="row mb-3">
                                        <div className="col-md-3">
                                            <UnlimitInput
                                                label={t("inputField.label.contactName")}
                                                id="contact_name"
                                                type="text"
                                                placeholder={t("inputField.placeholder.enterName")}
                                                register={register}
                                                rules={{
                                                    required: t("inputField.validation.required"),
                                                    pattern: {
                                                        value: /^[A-Za-z\s\-',.*&@]+$/,
                                                        message: t(
                                                            "inputField.validation.contactNamePattern"
                                                        ),
                                                    },
                                                }}
                                                error={errors.contact_name}
                                                errorMessage={errors.contact_name?.message}
                                            />
                                        </div>
                                        <div className="col-md-3">
                                            <UnlimitInput
                                                label={t("inputField.label.phone")}
                                                id="phone"
                                                type="text"
                                                placeholder={t("inputField.placeholder.enterPhone")}
                                                register={register}
                                                rules={{
                                                    pattern: {
                                                        value: /^(?:\+|00)?[\d\s\-()]+$/,
                                                        message: t("inputField.validation.phonePattern"),
                                                    },
                                                    maxLength: {
                                                        value: 20,
                                                        message: t("inputField.validation.maxLength", {
                                                            value: 20,
                                                        }),
                                                    },
                                                }}
                                                error={errors.phone}
                                                errorMessage={errors.phone?.message}
                                            />
                                        </div>
                                        <div className="col-md-3">
                                            <UnlimitInput
                                                label={t("inputField.label.email")}
                                                id="email"
                                                type="email"
                                                placeholder={t("inputField.placeholder.enterEmail")}
                                                register={register}
                                                rules={{
                                                    required: t("inputField.validation.required"),
                                                    pattern: {
                                                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                        message: t("inputField.validation.emailPattern"),
                                                    },
                                                }}
                                                error={errors.email}
                                                errorMessage={errors.email?.message}
                                            />
                                        </div>
                                    </div>

                                    {/* Address Information */}
                                    <div className="row mb-3">
                                        <div className="col-md-3">
                                            <UnlimitInput
                                                label={t("inputField.label.address")}
                                                id="address"
                                                type="text"
                                                placeholder={t("inputField.placeholder.enterAddress")}
                                                register={register}
                                                rules={{
                                                    required: t("inputField.validation.required"),
                                                    maxLength: {
                                                        value: 50,
                                                        message: t("inputField.validation.maxLength", {
                                                            value: 50,
                                                        }),
                                                    },
                                                }}
                                                error={errors.address}
                                                errorMessage={errors.address?.message}
                                            />
                                        </div>
                                        <div className="col-md-3">
                                            <UnlimitInput
                                                label={t("inputField.label.zipCode")}
                                                id="zip_code"
                                                type="text"
                                                placeholder={t("inputField.placeholder.enterZipCode")}
                                                register={register}
                                                rules={{
                                                    required: t("inputField.validation.required"),
                                                    pattern: {
                                                        value: /^[1-9][0-9]*$/,
                                                        message: t("inputField.validation.zipCodePattern"),
                                                    },
                                                    maxLength: {
                                                        value: 10,
                                                        message: t("inputField.validation.maxLength", {
                                                            value: 10,
                                                        }),
                                                    },
                                                }}
                                                error={errors.zip_code}
                                                errorMessage={errors.zip_code?.message}
                                            />
                                        </div>
                                        <div className="col-md-3">
                                            <UnlimitSelect
                                                label={t("inputField.label.country")}
                                                id="country_id"
                                                errors={errors}
                                                control={control}
                                                register={register}
                                                rules={{
                                                    required: t("inputField.validation.required"),
                                                }}
                                                placeholder={t("inputField.placeholder.selectCountry")}
                                                options={countries?.filter((country) => country.flag)}
                                                optionLabel="name"
                                                optionValue="id"
                                                onChange={onChangeCountry}
                                            />
                                        </div>
                                        <div className="col-md-3">
                                            <UnlimitSelect
                                                label={t("inputField.label.city")}
                                                id="city"
                                                errors={errors}
                                                control={control}
                                                register={register}
                                                options={cities}
                                                rules={{
                                                    required: t("inputField.validation.required"),
                                                }}
                                                placeholder={t("inputField.placeholder.selectCity")}
                                                optionLabel="name"
                                                optionValue="id"
                                                isLoading={citiesLoading}
                                                onChange={onChangeCity}
                                            />
                                        </div>
                                    </div>

                                    {/* Map Location */}
                                    <div className="mb-4">
                                        <label className="mb-2 fw-medium">
                                            {t("inputField.label.mapLocation")}
                                        </label>
                                        <GoogleMaps
                                            country={country}
                                            city={city}
                                            center={center}
                                            setCenter={setCenter}
                                        />
                                    </div>

                                    {/* Coordinates */}
                                    <div className="row mb-4">
                                        <div className="col-md-6">
                                            <UnlimitInput
                                                label={t("inputField.label.latitude")}
                                                id="latitude"
                                                type="text"
                                                placeholder={t("inputField.placeholder.enterLatitude")}
                                                register={register}
                                                value={center.lat}
                                                onChange={(event) =>
                                                    setCenter({
                                                        ...center,
                                                        lat: parseFloat(event.target.value),
                                                    })
                                                }
                                                rules={{
                                                    required: t("inputField.validation.required"),
                                                }}
                                                error={errors.latitude}
                                                errorMessage={errors.latitude?.message}
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <UnlimitInput
                                                label={t("inputField.label.longitude")}
                                                id="longitude"
                                                type="text"
                                                placeholder={t("inputField.placeholder.enterLongitude")}
                                                register={register}
                                                value={center.lng}
                                                onChange={(event) =>
                                                    setCenter({
                                                        ...center,
                                                        lng: parseFloat(event.target.value),
                                                    })
                                                }
                                                rules={{
                                                    required: t("inputField.validation.required"),
                                                }}
                                                error={errors.longitude}
                                                errorMessage={errors.longitude?.message}
                                            />
                                        </div>

                                    </div>

                                    {/* Timezone and Status */}
                                    <div className="row mb-3">
                                        <div className="col-md-6">
                                            <UnlimitSelect
                                                label={t("inputField.label.timeZone")}
                                                id="time_zone"
                                                errors={errors}
                                                control={control}
                                                register={register}
                                                rules={{
                                                    required: t("inputField.validation.required"),
                                                }}
                                                placeholder={t("inputField.placeholder.chooseTimeZone")}
                                                options={timezones}
                                                optionLabel="zoneName"
                                                optionValue="gmtOffsetName"
                                            />
                                        </div>
                                        <div className="col-md-6 ">
                                            <label className="form-label me-2">
                                                {t("inputField.label.status")}
                                            </label>
                                            <div className="d-flex">
                                                <UnlimitToggle
                                                    isChecked={watch("is_active")}
                                                    id="is_active"
                                                    register={register}
                                                />
                                            </div>
                                        </div>
                                    </div>


                                    {/* Controller Details */}
                                    <div className="mb-4">
                                        <h5 className="text-muted mb-3">
                                            {t("tableColumnName.controllerDetails")}
                                        </h5>
                                        <div className="row">
                                            <div className="col-md-3">
                                                <UnlimitInput
                                                    label={t("inputField.label.nSiteID")}
                                                    id="n_site_id"
                                                    type="number"
                                                    placeholder={t("inputField.placeholder.enterSiteID")}
                                                    register={register}
                                                    rules={{
                                                        required: t("inputField.validation.required"),
                                                    }}
                                                    error={errors.n_site_id}
                                                    errorMessage={errors.n_site_id?.message}
                                                />
                                            </div>
                                            <div className="col-md-3">
                                                <UnlimitInput
                                                    label={t("inputField.label.nControllerID")}
                                                    id="n_controller_id"
                                                    type="number"
                                                    placeholder={t(
                                                        "inputField.placeholder.enterControllerID"
                                                    )}
                                                    register={register}
                                                    rules={{
                                                        required: t("inputField.validation.required"),
                                                    }}
                                                    error={errors.n_controller_id}
                                                    errorMessage={errors.n_controller_id?.message}
                                                />
                                            </div>
                                            <div className="col-md-3">
                                                <UnlimitInput
                                                    label={t("inputField.label.nControlType")}
                                                    id="n_control_type"
                                                    type="text"
                                                    placeholder={t(
                                                        "inputField.placeholder.enterControlType"
                                                    )}
                                                    register={register}
                                                    rules={{
                                                        required: t("inputField.validation.required"),
                                                    }}
                                                    error={errors.n_control_type}
                                                    errorMessage={errors.n_control_type?.message}
                                                />
                                            </div>
                                            <div className="col-md-3">
                                                <UnlimitInput
                                                    label={t("inputField.label.nControlIndex")}
                                                    id="n_control_index"
                                                    type="number"
                                                    placeholder={t(
                                                        "inputField.placeholder.enterControlIndex"
                                                    )}
                                                    register={register}
                                                    rules={{
                                                        required: t("inputField.validation.required"),
                                                    }}
                                                    error={errors.n_control_index}
                                                    errorMessage={errors.n_control_index?.message}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Form Buttons */}
                                    <div className="d-flex gap-2 mb-4">
                                        <Link to={all_routes.outletsList}>
                                            <button type="button" className="btn btn-cancel">
                                                {t("general.button.cancel")}
                                            </button>
                                        </Link>
                                        <UnlimitSubmitButton
                                            label={
                                                id
                                                    ? t("general.button.update")
                                                    : t("general.button.submit")
                                            }
                                            isLoading={isLoading}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default OutletsForm;
